import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, CircleCheck, Dices } from 'lucide-react'
import { simulateConsortium } from '@/lib/consortium'
import { formatCurrency } from '@/lib/format'
import { buttonClassName } from '@/components/generals/button'
import { consortiumCategories } from '@/constants/consorcios'

const highlights = ['Sem juros', 'Sem entrada', 'FGTS em imóveis']

export const HeroSection: FC = (): ReactNode => {
    const featured = consortiumCategories[0]
    const example = simulateConsortium(featured, featured.credit.initial, featured.initialTerm)

    return (
        <section id='inicio' aria-labelledby='inicio-titulo' className='relative overflow-hidden'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-dot-grid [mask-image:radial-gradient(ellipse_at_top_left,black_5%,transparent_60%)]'
            />

            <div className='relative mx-auto grid max-w-7xl items-center gap-16 px-5 pb-24 pt-8 sm:px-8 sm:pt-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:pb-32 lg:pt-16'>
                <div className='motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-700'>
                    <p className='inline-flex items-center gap-2.5 rounded-full border border-border bg-card/80 px-4 py-1.5 text-sm font-semibold text-foreground/80 shadow-xs'>
                        <span aria-hidden='true' className='size-2 rounded-full bg-gold' />
                        Consórcio de imóveis, veículos e serviços
                    </p>

                    <h1
                        id='inicio-titulo'
                        className='mt-7 text-balance font-heading text-[3.2rem] leading-[0.98] tracking-tight text-foreground sm:text-7xl lg:text-[5.25rem]'
                    >
                        Sua casa, seu carro, seus planos. <em className='text-bronze'>Sem juros.</em>
                    </h1>

                    <p className='mt-7 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                        Planeje a conquista com parcelas que cabem no bolso e compre à vista quando for contemplado. A gente encontra o grupo certo e acompanha você até a chave na mão.
                    </p>

                    <div className='mt-10 flex flex-col gap-3 sm:flex-row'>
                        <Link href='#simulador' className={buttonClassName({ size: 'lg' })}>
                            Simular minha parcela
                            <ArrowRight aria-hidden='true' className='size-5' />
                        </Link>

                        <Link href='#contato' className={buttonClassName({ variant: 'outline', size: 'lg' })}>
                            Receber uma proposta
                        </Link>
                    </div>

                    <ul className='mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm font-semibold text-foreground/80'>
                        {highlights.map(item => (
                            <li key={item} className='flex items-center gap-2'>
                                <CircleCheck aria-hidden='true' className='size-5 text-bronze' />
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative mx-auto mb-10 w-full max-w-xl motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-700 lg:mb-0 lg:max-w-none'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-sand shadow-2xl shadow-ink/20 sm:aspect-[5/4] lg:aspect-[4/5]'>
                        <Image
                            src='/images/consorcio-imoveis.jpg'
                            alt='Casa moderna com amplas janelas iluminadas ao entardecer'
                            fill
                            preload
                            sizes='(min-width: 1280px) 560px, (min-width: 1024px) 45vw, (min-width: 640px) 576px, 100vw'
                            className='object-cover object-[60%_50%]'
                        />

                        <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-ink/50 via-ink/0 to-ink/0' />
                    </div>

                    <div className='absolute -right-2 top-8 hidden items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-primary-foreground shadow-xl sm:flex lg:-right-6'>
                        <span className='grid size-10 place-items-center rounded-xl bg-gold text-gold-foreground'>
                            <Dices aria-hidden='true' className='size-5' />
                        </span>

                        <div>
                            <p className='text-sm font-semibold'>
                                Sorteio ou lance
                            </p>

                            <p className='text-xs text-primary-foreground/70'>
                                Contemplações todo mês
                            </p>
                        </div>
                    </div>

                    <div className='absolute -bottom-10 left-4 right-4 rounded-2xl bg-card p-5 shadow-xl shadow-ink/15 ring-1 ring-border motion-safe:animate-float-soft sm:left-6 sm:right-auto sm:w-80 lg:-left-10'>
                        <div className='flex items-center justify-between gap-3'>
                            <p className='text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground'>
                                Carta de crédito
                            </p>

                            <span className='rounded-full bg-sand px-2.5 py-1 text-xs font-semibold text-foreground'>
                                {featured.name}
                            </span>
                        </div>

                        <p className='mt-2 font-heading text-4xl tabular-nums text-foreground'>
                            {formatCurrency(example.credit)}
                        </p>

                        <div className='mt-4 flex items-end justify-between gap-3 border-t border-border pt-4'>
                            <div>
                                <p className='text-xs text-muted-foreground'>
                                    Parcela estimada
                                </p>

                                <p className='mt-0.5 font-bold tabular-nums text-foreground'>
                                    {formatCurrency(example.installment, { cents: true })}
                                    <span className='font-medium text-muted-foreground'>/mês</span>
                                </p>
                            </div>

                            <p className='text-xs font-semibold text-muted-foreground'>
                                {example.term} meses
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
